import React, { useState, useMemo } from "react";
import {
  Plus,
  GripVertical,
  Pencil,
  Trash2,
  Circle,
  CheckCircle2,
  Archive,
  Users,
} from "lucide-react";
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { List, Task } from "../../../types";
import { useAuth } from "../../../contexts/AuthContext";
import { useUpdateListOrder } from "../../../hooks/mutations/useUpdateListOrder";
import { useSharedListIds } from "../../../hooks/queries/useSharedListIds";
import { interpolatedOrderPosition } from "../../../lib/orderPosition";
import SharedListMark from "../../ui/SharedListMark";
import ManageSharingDialog from "../ManageSharingDialog";

interface EditingList extends List {
  newName?: string;
}

interface ListsSettingsProps {
  lists: List[];
  tasks: Task[];
  editingList: EditingList | null;
  setEditingList: (list: EditingList | null) => void;
  onUpdateList: (oldName: string, newName: string, emoji: string) => void;
  onDeleteClick: (listName: string) => void;
  onAddList: () => void;
}

interface SortableListRowProps {
  list: List;
  tasks: Task[];
  editingList: EditingList | null;
  setEditingList: (list: EditingList | null) => void;
  onUpdateList: (oldName: string, newName: string, emoji: string) => void;
  onDeleteClick: (listName: string) => void;
  isShared: boolean;
  onManageSharing: (list: List) => void;
}

const SortableListRow: React.FC<SortableListRowProps> = ({
  list,
  tasks,
  editingList,
  setEditingList,
  onUpdateList,
  onDeleteClick,
  isShared,
  onManageSharing,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: list.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const breakdown = useMemo(() => {
    let open = 0;
    let completed = 0;
    let archived = 0;
    for (const t of tasks) {
      if (t.list_id !== list.id) continue;
      if (t.archived) archived++;
      else if (t.completed) completed++;
      else open++;
    }
    return { open, completed, archived };
  }, [tasks, list.id]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`px-3 py-2.5 transition-colors ${isHovered ? "bg-warm-border-subtle/50" : ""}`}
    >
      {editingList?.name === list.name ? (
        <div className="space-y-2.5">
          <div className="grid grid-cols-4 gap-2">
            <input
              type="text"
              value={editingList.emoji}
              onChange={(e) =>
                setEditingList({
                  ...editingList,
                  emoji: e.target.value,
                })
              }
              maxLength={2}
              className="bg-warm-bg-elevated border border-warm-border rounded-lg px-2 py-1.5 text-lg text-center text-warm-text-primary focus:outline-none focus:ring-2 focus:ring-warm-blue"
            />
            <input
              type="text"
              value={editingList.newName || editingList.name}
              onChange={(e) =>
                setEditingList({
                  ...editingList,
                  newName: e.target.value,
                })
              }
              className="col-span-3 bg-warm-bg-elevated border border-warm-border rounded-lg px-3 py-1.5 text-sm text-warm-text-primary focus:outline-none focus:ring-2 focus:ring-warm-blue"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setEditingList(null)}
              className="flex-1 px-3 py-1.5 bg-warm-bg-elevated hover:bg-warm-border-subtle text-warm-text-primary rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() =>
                onUpdateList(
                  list.name,
                  editingList.newName || editingList.name,
                  editingList.emoji
                )
              }
              className="flex-1 px-3 py-1.5 bg-warm-blue hover:bg-warm-blue-dark text-white rounded-lg text-sm transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div
              className={`text-warm-text-tertiary cursor-grab active:cursor-grabbing transition-opacity duration-150 flex-shrink-0 ${
                isHovered ? "opacity-100" : "opacity-0"
              }`}
              {...attributes}
              {...listeners}
            >
              <GripVertical size={15} />
            </div>
            <span className="text-base leading-none">{list.emoji}</span>
            <span className="text-sm text-warm-text-primary font-medium">{list.name}</span>
            {isShared && <SharedListMark size={12} />}
            <span className="flex items-center gap-1 ml-1">
              {breakdown.open > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-xs text-warm-text-tertiary bg-warm-border-subtle px-1.5 py-0.5 rounded-full tabular-nums"
                  aria-label={`${breakdown.open} open`}
                >
                  <Circle size={10} aria-hidden="true" />
                  {breakdown.open}
                </span>
              )}
              {breakdown.completed > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-xs text-warm-text-tertiary bg-warm-border-subtle px-1.5 py-0.5 rounded-full tabular-nums"
                  aria-label={`${breakdown.completed} completed`}
                >
                  <CheckCircle2 size={10} aria-hidden="true" />
                  {breakdown.completed}
                </span>
              )}
              {breakdown.archived > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-xs text-warm-text-tertiary bg-warm-border-subtle px-1.5 py-0.5 rounded-full tabular-nums"
                  aria-label={`${breakdown.archived} archived`}
                >
                  <Archive size={10} aria-hidden="true" />
                  {breakdown.archived}
                </span>
              )}
            </span>
          </div>
          <div className={`flex items-center gap-0.5 transition-opacity duration-150 ${isHovered ? "opacity-100" : "opacity-0"}`}>
            <button
              onClick={() => onManageSharing(list)}
              aria-label={isShared ? `Manage sharing for ${list.name}` : `Share ${list.name}`}
              className="p-1.5 rounded-md text-warm-text-tertiary hover:text-warm-text-primary hover:bg-warm-border-subtle transition-colors"
            >
              <Users size={13} />
            </button>
            <button
              onClick={() => setEditingList({ ...list, newName: list.name })}
              aria-label={`Edit ${list.name}`}
              className="p-1.5 rounded-md text-warm-text-tertiary hover:text-warm-text-primary hover:bg-warm-border-subtle transition-colors"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={() => onDeleteClick(list.name)}
              aria-label={`Delete ${list.name}`}
              className="p-1.5 rounded-md text-warm-text-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ListsSettings: React.FC<ListsSettingsProps> = ({
  lists,
  tasks,
  editingList,
  setEditingList,
  onUpdateList,
  onDeleteClick,
  onAddList,
}) => {
  const { user } = useAuth();
  const updateListOrder = useUpdateListOrder();
  const { data: sharedListIds } = useSharedListIds();
  const [managingList, setManagingList] = useState<List | null>(null);

  // Lists this person *owns*. Since sharing, `lists` also carries the Lists
  // other people share with them, and every control on this tab — rename,
  // reorder, delete, manage sharing — is an Owner-only act the database
  // refuses without a word. Those Lists have their own tab ("Shared with
  // me"), and iOS's ManageListsView filters the same way.
  const ownedLists = useMemo(
    () => lists.filter((l) => l.user_id === user?.id),
    [lists, user],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const listIds = useMemo(() => ownedLists.map((l) => l.id), [ownedLists]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const draggedList = ownedLists.find((l) => l.id === active.id);
    const targetList = ownedLists.find((l) => l.id === over.id);
    if (!draggedList || !targetList) return;

    const draggedIndex = ownedLists.findIndex((l) => l.id === draggedList.id);
    const targetIndex = ownedLists.findIndex((l) => l.id === targetList.id);

    updateListOrder.mutate({
      listId: draggedList.id,
      newOrderPosition: Math.floor(
        interpolatedOrderPosition(ownedLists, draggedIndex, targetIndex),
      ),
    });
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-lg font-semibold text-warm-text-primary">Lists</h3>
        {ownedLists.length > 0 && (
          <div className="flex items-center gap-3 text-xs text-warm-text-tertiary">
            <span className="inline-flex items-center gap-1">
              <Circle size={11} aria-hidden="true" /> Open
            </span>
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 size={11} aria-hidden="true" /> Completed
            </span>
            <span className="inline-flex items-center gap-1">
              <Archive size={11} aria-hidden="true" /> Archived
            </span>
          </div>
        )}
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={listIds} strategy={verticalListSortingStrategy}>
          <div className="rounded-xl border border-warm-border bg-warm-bg-elevated divide-y divide-warm-border mb-4 overflow-hidden">
            {ownedLists.map((list) => (
              <SortableListRow
                key={list.id}
                list={list}
                tasks={tasks}
                editingList={editingList}
                setEditingList={setEditingList}
                onUpdateList={onUpdateList}
                onDeleteClick={onDeleteClick}
                isShared={!!sharedListIds?.has(list.id)}
                onManageSharing={setManagingList}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {managingList && (
        <ManageSharingDialog list={managingList} onClose={() => setManagingList(null)} />
      )}

      <button
        onClick={onAddList}
        className="w-full px-4 py-2 bg-warm-blue hover:bg-warm-blue-dark text-white rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
      >
        <Plus size={16} />
        Add New List
      </button>
    </div>
  );
};

export default ListsSettings;
